import { QueryFactory } from '@lmt/shared-types';
import {
  DataType,
  DictionaryRecordDto,
  DataBlockTypeDto,
  MergeFieldDto,
  MergeFieldQueryType,
} from '@lmt/shared-types';

import {
  Field,
  FieldLinkProps,
} from '@lmt/unicorn-shared/app/components/common/Field';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { MergeFieldGroupDto } from '@lmt/unicorn-shared/app/components/documents/dto/MergeFieldGroupDto';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { dfnMergeFieldData } from '@lmt/unicorn-shared/app/components/tenant-settings/components/general/types';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '../../merge-assistant/form/components/common';

export const mergeFieldsQueryKey = 'merge-fields';

export function doesFilterContainAnotherFilter(
  filter: any,
  subFilter: any,
): boolean {
  if (!filter) return false;
  if (!subFilter) return false;
  if (!(subFilter instanceof Array)) {
    throw new Error(
      `Invalid subFilter, should be array: ${JSON.stringify(subFilter)}`,
    );
  }
  if (subFilter.length !== 3) {
    throw new Error(
      `Invalid subFilter length, should be 3: ${JSON.stringify(subFilter)}`,
    );
  }

  if (!(filter instanceof Array)) {
    throw new Error(`Invalid part of filter: ${JSON.stringify(filter)}`);
  }
  if (filter.length === 0) return false;

  if (
    filter.length === 3 &&
    !Array.isArray(filter[0]) &&
    !Array.isArray(filter[2])
  ) {
    if (
      filter[0] === subFilter[0] &&
      filter[1] === subFilter[1] &&
      filter[2] === subFilter[2]
    ) {
      return true;
    } else {
      return false;
    }
  }

  if (filter.length % 2 !== 1) {
    throw new Error(
      `Only even number of elements in filter is currently supported. Found ${
        filter.length
      } elements. Filter: ${JSON.stringify(filter)}`,
    );
  }

  if (doesFilterContainAnotherFilter(filter[0], subFilter)) return true;

  let currentOperatorIndex = 1;
  while (currentOperatorIndex < filter.length) {
    if (doesFilterContainAnotherFilter(filter[0], subFilter)) return true;

    currentOperatorIndex += 2;
  }

  return false;
}

export function convertFilterToString(filterArray: any): string {
  if (!(filterArray instanceof Array)) {
    throw new Error(`Invalid part of filter: ${JSON.stringify(filterArray)}`);
  }
  if (filterArray.length === 0) return '';

  if (
    filterArray.length === 3 &&
    !Array.isArray(filterArray[0]) &&
    !Array.isArray(filterArray[2])
  ) {
    if (typeof filterArray[2] === 'string') {
      filterArray[2] = `'${filterArray[2]}'`;
    }
    if (filterArray[2] === null) {
      filterArray[2] = 'null';
    }

    return `${filterArray.join(' ')}`;
  }

  if (filterArray.length % 2 !== 1) {
    throw new Error(
      `Only even number of elements in filter is currently supported. Found ${
        filterArray.length
      } elements. Filter: ${JSON.stringify(filterArray)}`,
    );
  }

  let result = `(${convertFilterToString(filterArray[0])})`;
  let currentOperatorIndex = 1;
  while (currentOperatorIndex < filterArray.length) {
    result += ` ${filterArray[currentOperatorIndex]} (${convertFilterToString(
      filterArray[currentOperatorIndex + 1],
    )})`;

    currentOperatorIndex += 2;
  }

  return result;
}

export function getMeasurementTypeNameFromFilter(
  filterArray: any,
  measurementTypes: DictionaryRecordDto[],
): string | undefined {
  if (!(filterArray instanceof Array) || filterArray.length !== 3) {
    return undefined;
  }
  if (filterArray[0] === 'measurement.typeId' && filterArray[1] === '=') {
    return measurementTypes.find((type) => type.id === filterArray[2])?.value;
  }
  return undefined;
}

export function convertToDxDataType(
  dataType: DataType,
): 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime' {
  switch (dataType) {
    case DataType.Boolean:
      return 'boolean';

    case DataType.DateOnly:
    case DataType.DateTime:
    case DataType.TimeOnly:
      return 'datetime';

    case DataType.Number:
      return 'number';

    case DataType.Text:
    case DataType.MultilineText:
      return 'string';

    case DataType.Dictionary:
    case DataType.Table:
      return 'string';

    case DataType.Undefined:
      return 'string';

    default:
      assertNever(dataType);
  }
}

function includesAllWords(value: string, searchString: string) {
  const searchWords = searchString.toLowerCase().split(/(\s+)/);
  return searchWords
    .filter((x) => !isEmpty(x.trim()))
    .every((x) => value.toLowerCase().includes(x.trim()));
}

export function mergeFieldCodeAsString(mergeField: MergeFieldDto): string {
  if ('items' in (mergeField as MergeFieldGroupDto)) {
    return '';
  }
  return (
    mergeField.prefix +
    (mergeField.code > 9999
      ? mergeField.code.toString()
      : ('0000' + mergeField.code).slice(-4))
  );
}

export function getMergeFieldsGroups(
  fieldItems: MergeFieldDto[],
  searchText: string,
  dataBlockTypes: Record<number, DataBlockTypeDto>,
): MergeFieldDto[] {
  const filteredFields = fieldItems.filter(
    (field) =>
      !searchText ||
      includesAllWords(field.name, searchText) ||
      mergeFieldCodeAsString(field)
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      includesAllWords(field.expression, searchText),
  );
  return filteredFields;
  // const groupedQueries = groupBy(
  //   filteredFields,
  //   (item: MergeFieldDto) => item.query.dataBlockTypeId,
  // );
  // Object.keys(groupedQueries).forEach((key) => {
  //   const value = groupedQueries[key];
  //   delete groupedQueries[key];
  //
  //   const dataBlockTypeId = parseInt(key);
  //   const queryIdentifier = {
  //     queryType: isNaN(dataBlockTypeId)
  //       ? MergeFieldQueryType.PatientAndSharedCaseQuery
  //       : MergeFieldQueryType.DataBlockQuery,
  //     dataBlockTypeId: dataBlockTypeId,
  //     dataBlockType: null,
  //   };
  //   const localizedKey = queryIdentifierToString(
  //     queryIdentifier,
  //     dataBlockTypes,
  //   );
  //   groupedQueries[localizedKey] = value;
  // });
  // return getMergeFieldGroupsFromDictionary(groupedQueries, []);
}

// convert Dictionary<key: string, items: MergeFieldDto[]> to MergeFieldGroupDto with nested items (MergeFieldDto[])
// function getMergeFieldGroupsFromDictionary(
//   groups: Dictionary<MergeFieldDto[]>,
//   usedIds: number[],
// ) {
//   const groupedFields: MergeFieldGroupDto[] = [];
//   for (const key in groups) {
//     const items = groups[key];
//     const group = ;
//     // all elements should have unique id
//     // we use negative values for virtual elements (group headers, e.g. Patients, Cases)
//     group.id = usedIds?.length > 0 ? Math.min(...usedIds) - 1 : -1;
//     usedIds.push(group.id);
//     group.name = key;
//     group.items = items;
//     groupedFields.push(group);
//   }
//   return groupedFields;
// }

export function queryIdentifierToString(
  queryIdentifier: QueryFactory.MergeFieldQueryDto,
  data: Record<number, DataBlockTypeDto>,
): string {
  if (queryIdentifier.queryType === MergeFieldQueryType.VisitQuery) {
    return i18n.t('translation:documents.visit');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.CaseQuery) {
    return i18n.t('translation:documents.case');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.PatientQuery) {
    return i18n.t('translation:documents.patient');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.HL7Settings) {
    return i18n.t('translation:documents.hl7');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.GDTSettings) {
    return i18n.t('translation:documents.gdt');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.FileQuery) {
    return i18n.t('translation:documents.file');
  }
  if (queryIdentifier.queryType === MergeFieldQueryType.DataBlockQuery) {
    if (queryIdentifier.dataBlockTypeId === null) {
      return '';
    }
    const dataBlockType = data[queryIdentifier.dataBlockTypeId];
    if (!dataBlockType) {
      return '';
    }
    return (
      i18n.t(('translation:data_block_type.' + dataBlockType.variant) as any) +
      ' - ' +
      dataBlockType.value
    );
  }
  assertNever(queryIdentifier.queryType);
}

export const useOnClone = () => {
  const modals = useModal();
  const globalForms = useGlobalForms();
  const i18next = useScopedTranslation('documents.query_builder');
  const queryClient = useQueryClient();

  return async (
    mergeField: MergeFieldDto,
    options?: {
      onSuccess?: () => void;
      onLoading?: () => void;
      onFinish?: () => void;
      onError?: () => void;
    },
  ) => {
    if (!mergeField) {
      // normally Clone button will not be shown in this case
      return;
    }

    const result = await modals.showCustom<{
      name: string;
      prefix: string;
      mergeFieldId?: number;
      nameTextError?: string;
    }>({
      validate: async (value) => {
        if (!value) {
          return false;
        }

        if (!value.name.trim()) {
          value.nameTextError = i18n.t('errors.required');
          return false;
        }

        return true;
      },
      defaultValue: {
        mergeFieldId: mergeField.id,
        name: mergeField.name + ' (Copy)',
        prefix:
          mergeField.isDefault || dfnMergeFieldData.prefix === mergeField.prefix
            ? 'U'
            : mergeField.prefix, // if prefix of the copied field was default, change it to U
      },

      Component: (props) => {
        const prefixes = QueryFactory.MergeFieldPrefixQuery.useGetQuery({
          isUserPrefix: true,
        });

        const linkOptions = usePrefixLinkOptions((newPrefix) => {
          props.setValue({ ...props.value!, prefix: newPrefix });
        });

        return (
          <div>
            <Field
              title={i18next.t('prefix_field_title')}
              className={globalFormStyles.field}
              required={true}
              linkProps={linkOptions}
              hint={i18next.t('prefix_hint')}
            >
              <DropDownInput
                required={true}
                variant={'formInput'}
                options={prefixes.data?.prefixes ?? []}
                value={props.value?.prefix ?? ''}
                onValueChanged={(value) =>
                  props.setValue({ ...props.value!, prefix: value })
                }
                testId="prefix"
              />
            </Field>

            <Field
              title={i18next.t('name_field_title')}
              required={true}
              className={globalFormStyles.field}
            >
              <Input
                autoFocus={true}
                value={props.value?.name ?? ''}
                onChange={(e) =>
                  props.setValue({
                    ...props.value!,
                    name: e.target.value,
                    nameTextError: undefined,
                  })
                }
                errorText={props.value?.nameTextError}
              />
            </Field>
          </div>
        );
      },
      title: i18next.t('copy_form_title'),
    });

    if (!result) {
      return;
    }

    try {
      options?.onLoading?.();
      const newMergeField = await QueryFactory.MergeFieldClient.create({
        ...mergeField,
        prefix: result.prefix,
        name: result.name.trim(),
        code: undefined,
      });

      await queryClient.invalidateQueries({
        queryKey: QueryFactory.MergeFieldQuery.getQueriesQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.MergeFieldQuery.getQueryKey(),
      });

      options?.onSuccess?.();
      globalForms.editMergeField(newMergeField.id);
    } catch (e) {
      options?.onError?.();
      await modals.showError({ text: convertToErrorString(e) });
    } finally {
      options?.onFinish?.();
    }
  };
};

export const usePrefixLinkOptions = (
  onNewPrefixSelected?: (prefix: string) => void,
): FieldLinkProps => {
  const modals = useModal();
  const i18n = useTranslation();
  const i18next = useScopedTranslation('documents.query_builder');
  const queryClient = useQueryClient();

  return {
    title: i18next.t('add_prefix'),
    onClick: async () => {
      const prefix = await modals.showCustom<{
        prefix: string;
        prefixTextError?: string;
      }>({
        defaultValue: {
          prefix: '',
        },
        Component: (props) => {
          return (
            <div>
              <Field
                className={globalFormStyles.field}
                title={i18next.t('add_prefix_title')}
                required={true}
                testId="new-code-prefix"
              >
                <Input
                  required={true}
                  autoFocus={true}
                  value={props.value?.prefix}
                  onChange={(e) => {
                    props.setValue({
                      ...props.value,
                      prefixTextError: undefined,
                      prefix: e.target.value,
                    });
                  }}
                  errorText={props.value?.prefixTextError}
                />
              </Field>
            </div>
          );
        },
        title: i18n.t('global_forms.create_code_prefix'),
        validate: async (value) => {
          if (!value) {
            return false;
          }

          if (!value.prefix) {
            value.prefixTextError = i18n.t('errors.required');
            return false;
          }

          if (value.prefix.trim().includes(' ')) {
            value.prefixTextError = i18n.t('errors.no_spaces');
            return false;
          }

          try {
            await QueryFactory.MergeFieldPrefixClient.create({
              prefix: value.prefix,
            });

            return true;
          } catch (error: any) {
            value.prefixTextError = convertToErrorString(error, {
              excludeFieldName: true,
            });
            return false;
          }
        },
      });
      if (!prefix) return;

      try {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.MergeFieldPrefixQuery.getQueryKey(),
        });

        if (onNewPrefixSelected) {
          onNewPrefixSelected(prefix.prefix.toUpperCase());
        }
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
  };
};
